import axios, { type AxiosError } from 'axios';

import { env } from '@/env';
import { useAuthStore } from '@/store/authStore';

export const apiClient = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT from Zustand store before every request. Admin gating now
// runs server-side off the user's `isAdmin` flag — no header needed.
// Never overwrite an Authorization header the caller set explicitly —
// the login flow passes the freshly-issued token before the store has
// been updated, and a stale token in localStorage would otherwise
// clobber it and cause /auth/me to 401.
apiClient.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // DEBUG: log every outgoing request
  console.debug('[API →]', config.method?.toUpperCase(), config.url, {
    baseURL: config.baseURL,
    headers: config.headers,
    data: config.data as unknown,
  });
  return config;
});

// Handle 401 globally — clear auth state and redirect to login
apiClient.interceptors.response.use(
  (response) => {
    // DEBUG: log every successful response
    console.debug('[API ←]', response.status, response.config.url, response.data);
    return response;
  },
  (error: AxiosError) => {
    // DEBUG: log every error response
    console.error('[API ✗]', error.response?.status, error.config?.url, {
      requestHeaders: error.config?.headers,
      responseData: error.response?.data,
    });
    // 401 on the login/register endpoints means bad credentials — don't
    // clear the store or redirect (the form shows the error inline).
    // Every other 401 means the stored token is stale/invalid, including
    // /auth/me — clear and bounce to /login so the app self-heals.
    const url = error.config?.url ?? '';
    const isCredentialCheck =
      url.startsWith('/auth/email-login') ||
      url.startsWith('/auth/register') ||
      url === '/auth/google';
    if (error.response?.status === 401 && !isCredentialCheck) {
      useAuthStore.getState().clearAuth();
      // Stash the page the user was on so /login (and the Google
      // callback that follows) can send them back after re-auth.
      // sessionStorage instead of react-router state because this
      // is a hard `window.location.replace` — router state doesn't
      // survive a full page load.
      try {
        const here = window.location.pathname + window.location.search;
        if (!here.startsWith('/login') && !here.startsWith('/auth/callback')) {
          sessionStorage.setItem('auth-redirect-from', here);
        }
      } catch {
        /* private mode: sessionStorage may throw — fine, admin lands on /dashboard */
      }
      window.location.replace('/login');
    }
    return Promise.reject(error);
  },
);
